import { CartAddEvent } from '@theme/events';

async function vznAddToCart(variantId, quantity, productId) {
  const formData = new FormData();
  formData.set('id', String(variantId));
  formData.set('quantity', String(quantity));
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/javascript' },
    body: formData,
  });
  if (!response.ok && response.status !== 422) throw new Error('Could not add to cart. Please try again.');
  const json = await response.json();
  if (json.status) throw new Error(json.description || json.message || 'Could not add to cart');

  document.dispatchEvent(
    new CartAddEvent({}, `vzn-${variantId}`, {
      source: 'product-form-component',
      itemCount: quantity,
      productId: String(productId || json.product_id || ''),
      variantId: String(variantId),
      sections: json.sections,
    })
  );
  return json;
}

class VznProductForm extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-vzn-variants]');
    let variants = [];
    try {
      variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch {
      variants = [];
    }
    this.variants = variants;
    this.idInput = this.querySelector('input[name="id"]');
    this.button = this.querySelector('.vzn-atc');
    this.errorEl = this.querySelector('.vzn-form-error');
    this.addEventListener('change', (event) => {
      if (event.target.matches('.vzn-pill-input, .vzn-variant-select')) this.syncVariant();
    });
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
    } else if (this.button) {
      this.button.addEventListener('click', () => this.submit());
    }
    this.syncVariant();
  }

  get selectedVariant() {
    if (this.variants.length === 1) return this.variants[0];
    const select = this.querySelector('.vzn-variant-select');
    if (select) return this.variants.find((v) => String(v.id) === select.value) || null;
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-index]'));
    if (!fieldsets.length) return this.variants[0] || null;
    const picked = fieldsets.map((fs) => {
      const checked = fs.querySelector('.vzn-pill-input:checked');
      return checked ? checked.value : null;
    });
    if (picked.includes(null)) return null;
    return this.variants.find((v) => v.options.every((opt, i) => opt === picked[i])) || null;
  }

  syncVariant() {
    const variant = this.selectedVariant;
    if (!this.button) return;
    if (!variant) {
      this.button.disabled = true;
      return;
    }
    if (this.idInput) this.idInput.value = String(variant.id);
    const current = this.querySelector('.vzn-price__current');
    if (current) current.textContent = variant.price;
    const compare = this.querySelector('.vzn-price__compare');
    if (compare) compare.style.display = variant.compare ? '' : 'none';
    if (compare && variant.compare) compare.textContent = variant.compare;
    const save = this.querySelector('.vzn-price__save');
    if (save) save.style.display = variant.save ? '' : 'none';
    if (save && variant.save) save.textContent = `Save ${variant.save}`;
    this.button.disabled = !variant.available;
    const label = this.button.querySelector('.vzn-atc__label');
    if (label) label.textContent = variant.available ? this.dataset.labelAdd || 'ADD TO CART' : 'SOLD OUT';
    if (this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }
  }

  async submit() {
    const variant = this.selectedVariant;
    if (!variant || !variant.available || !this.button) return;
    const label = this.button.querySelector('.vzn-atc__label');
    const originalText = label ? label.textContent : '';
    this.button.disabled = true;
    if (label) label.textContent = 'ADDING…';
    this.errorEl?.classList.remove('is-visible');
    try {
      await vznAddToCart(variant.id, 1, this.dataset.productId);
      this.button.classList.add('is-added');
      if (label) label.textContent = 'ADDED ✓';
      setTimeout(() => {
        this.button.classList.remove('is-added');
        if (label) label.textContent = originalText;
        this.button.disabled = false;
      }, 2000);
    } catch (error) {
      if (this.errorEl) {
        this.errorEl.textContent = error.message;
        this.errorEl.classList.add('is-visible');
      }
      if (label) label.textContent = originalText;
      this.button.disabled = false;
    }
  }
}

class VznGallery extends HTMLElement {
  connectedCallback() {
    this.mainImg = this.querySelector('.vzn-gallery__main img');
    this.addEventListener('click', (event) => {
      const thumb = event.target.closest('.vzn-gallery__thumb');
      if (!thumb || !this.mainImg) return;
      this.querySelectorAll('.vzn-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      this.mainImg.src = thumb.dataset.full;
      this.mainImg.srcset = thumb.dataset.fullSrcset || '';
    });
    const track = this.querySelector('.vzn-gallery__track');
    const dots = Array.from(this.querySelectorAll('.vzn-gallery__dot'));
    if (track && dots.length) {
      track.addEventListener(
        'scroll',
        () => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        },
        { passive: true }
      );
    }
  }
}

class VznStickyAtc extends HTMLElement {
  connectedCallback() {
    const scope = this.closest('.vzn-main') || document;
    const anchor = scope.querySelector('[data-vzn-sticky-anchor]');
    if (!anchor) return;
    new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', !entry.isIntersecting);
    }).observe(anchor);
  }
}

class VznCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.vzn-carousel__track');
    this.querySelector('.vzn-carousel__arrow--prev')?.addEventListener('click', () => this.scrollByCards(-1));
    this.querySelector('.vzn-carousel__arrow--next')?.addEventListener('click', () => this.scrollByCards(1));
  }
  scrollByCards(direction) {
    if (!this.track) return;
    const card = this.track.firstElementChild;
    const step = card ? (card.getBoundingClientRect().width + 16) * 2 : 600;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }
}

class VznRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-eager')) {
      this.load();
      return;
    }
    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        this.load();
      },
      { rootMargin: '400px' }
    );
    observer.observe(this);
  }
  async load() {
    const wrap = this.closest('[data-vzn-rec-wrap]') || this.closest('.vzn-section');
    try {
      const response = await fetch(this.dataset.url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('vzn-recommendations');
      if (fresh && fresh.children.length > 0) {
        this.innerHTML = fresh.innerHTML;
        wrap?.removeAttribute('hidden');
      } else {
        wrap?.setAttribute('hidden', '');
      }
    } catch {
      wrap?.setAttribute('hidden', '');
    }
  }
}

class VznVideoTile extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    video.muted = true;
    new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.4 }
    ).observe(this);
  }
}

class VznAccordion extends HTMLElement {
  connectedCallback() {
    const trigger = this.querySelector('.vzn-accordion__trigger');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', String(this.classList.contains('is-open')));
    trigger.addEventListener('click', () => {
      const open = this.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(open));
    });
  }
}

customElements.define('vzn-product-form', VznProductForm);
customElements.define('vzn-gallery', VznGallery);
customElements.define('vzn-sticky-atc', VznStickyAtc);
customElements.define('vzn-carousel', VznCarousel);
customElements.define('vzn-recommendations', VznRecommendations);
customElements.define('vzn-video-tile', VznVideoTile);
customElements.define('vzn-accordion', VznAccordion);
